/**
 * session-store.ts — what the console keeps about the session, which is no
 * longer a credential.
 *
 * ### This file replaces `tokens.ts`, and the rename is the point
 *
 * It used to hold the access and refresh tokens in `sessionStorage`, with a
 * comment admitting that "neither is safe against XSS — any script running on
 * the page can read both — and the genuinely safe answer is an httpOnly cookie".
 * That is now what happens: `POST /api/auth/login` answers with `Set-Cookie` and
 * a body containing no token at all, and the browser attaches the session to
 * every same-origin request without this code — or any other code on the page —
 * ever seeing it. A file called `tokens` that holds no tokens would be the kind
 * of name that survives a refactor and misleads for a year.
 *
 * ### What is left here, and why it is safe
 *
 * 1. **A display hint** — the signed-in operator's name, email and role. Not a
 *    credential and never trusted by anything: the server reads the role from
 *    the token's claims on every request, so an operator who edits this to say
 *    `admin` gains exactly nothing (`app/session.tsx` has said so since Phase 4;
 *    it is now also true of the thing an attacker would rather have). It exists
 *    so a reload renders the console immediately instead of flashing the login
 *    screen while `/auth/me` answers.
 *
 * 2. **A reader for the CSRF cookie**, which is deliberately NOT `httpOnly`
 *    because the console has to echo it in `X-CSRF-Token`. On its own it
 *    authenticates nothing — it is worthless without the session cookie, which
 *    no script can read. That asymmetry is the whole double-submit design.
 *
 * `sessionStorage` rather than `localStorage` for the hint, for the reason the
 * old file gave: a console session ends with the tab. The session COOKIE has the
 * same property by construction — it carries no `Max-Age`, so it dies with the
 * browser session.
 */
import type { AuthUser } from './api';

const USER_KEY = 'nafis-session';

/**
 * Both names the CSRF cookie can have.
 *
 * The server emits `__Host-nafis_csrf` over HTTPS (the prefix locks the cookie
 * to this exact host, so no sibling `*.warsha.ai` deployment can overwrite it)
 * and the bare name over plain HTTP, because `__Host-` mandates `Secure` and a
 * `Secure` cookie is silently discarded on an insecure origin. The console
 * accepts either, so `npm run dev` and the deployed stack run the same code.
 */
const CSRF_COOKIE_NAMES = ['__Host-nafis_csrf', 'nafis_csrf'];

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    // Storage disabled (private mode, hardened browser). The hint simply will
    // not survive a reload — `/auth/me` re-discovers the session — and that
    // must not be a crash.
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // As above.
  }
}

/**
 * The CSRF token the server issued for this session, or `null`.
 *
 * Read fresh on every request rather than cached: a refresh re-sends the cookie,
 * and a value cached at module load would outlive a sign-out — sending a stale
 * token would fail as a forged one, which is the confusing way for a logged-out
 * console to break.
 */
export function csrfToken(): string | null {
  const jar = typeof document === 'undefined' ? '' : document.cookie;
  for (const name of CSRF_COOKIE_NAMES) {
    for (const part of jar.split(';')) {
      const eq = part.indexOf('=');
      if (eq < 1) continue;
      if (part.slice(0, eq).trim() !== name) continue;
      try {
        return decodeURIComponent(part.slice(eq + 1).trim()) || null;
      } catch {
        return part.slice(eq + 1).trim() || null;
      }
    }
  }
  return null;
}

export const session = {
  /** Who the console believes is signed in. A hint; the server decides. */
  user(): AuthUser | null {
    const raw = read(USER_KEY);
    if (!raw) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && typeof (parsed as AuthUser).email === 'string') {
        return parsed as AuthUser;
      }
    } catch {
      // Malformed storage means "not signed in", never a crash.
    }
    return null;
  },

  setUser(user: AuthUser): void {
    write(USER_KEY, JSON.stringify({
      // Copied field by field so a richer payload (the profile carries a locale)
      // cannot quietly widen what is persisted on the operator's machine.
      userId: user.userId,
      email: user.email,
      role: user.role,
      name: user.name,
    }));
  },

  /**
   * Forget the local hint.
   *
   * It does NOT end the session — it cannot, because the credential is an
   * `httpOnly` cookie this code has no access to. `POST /api/auth/logout` is
   * what ends it, by revoking the refresh token server-side and clearing the
   * cookies. Calling only this would leave a live session with a console that
   * merely looked signed out.
   */
  clear(): void {
    write(USER_KEY, null);
  },
};
